import React, {
  FC,
  useRef,
  useState,
  useMemo,
  useEffect,
  Fragment,
} from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Vector2 } from 'three';
import {
  Lights,
  CanvasBackground,
  Planet,
  Dialog,
} from '../../../../components';
import { repeat } from '../../../../utils';
import { NUM_LEVELS_PER_STAGE, STAGES } from '../../../../traits';
import { useCamera } from '../../../../utils';

import { extend } from '@react-three/fiber';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';

extend({ EffectComposer, RenderPass, OutlinePass, ShaderPass });

export type StarMapProps = {
  level: number;
  maxLevel: number;
  onComplete: (nextLevel: number) => void;
};
export const StarMap: FC<StarMapProps> = ({ level, maxLevel, onComplete }) => {
  const planetsRef = useRef<any[]>([]);
  const [targetLevel, setTargetLevel] = useState<number>(level);
  useCamera([0, -4000, 0], [0, 0, 0], 45);

  const { gl, scene, camera, size } = useThree();
  const composer = useRef<any>();
  const aspect = useMemo(() => new Vector2(size.width, size.height), [size]);
  useEffect(() => composer.current.setSize(size.width, size.height), [size]);
  useFrame(() => composer.current.render(), 1);

  const stage = STAGES[Math.floor(targetLevel / NUM_LEVELS_PER_STAGE)];

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          setTargetLevel(Math.max(0, targetLevel - 1));
          break;
        case 'ArrowUp':
          setTargetLevel(Math.min(maxLevel, targetLevel + 1));
          break;
        case 'Enter':
          onComplete(targetLevel);
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [targetLevel, onComplete, setTargetLevel, maxLevel]);

  return (
    <>
      <Lights />
      <CanvasBackground texture={stage.background} />
      {stage.levels.map(({ planet, starMapX }, i) => {
        const l = (targetLevel % NUM_LEVELS_PER_STAGE) + i;
        const z = ((i + 1) * 3800) / (stage.levels.length + 1) - 1900;
        return (
          <Fragment key={i}>
            <Planet
              groupRef={el => {
                planetsRef.current[i] = el;
              }}
              onPointerOver={() => {
                if (l <= maxLevel) setTargetLevel(l);
              }}
              onClick={() => {
                if (l <= maxLevel) onComplete(l);
              }}
              key={i}
              position={[starMapX * 300 - 1500, 0, z]}
              {...planet}
            />
            {i === targetLevel % NUM_LEVELS_PER_STAGE && (
              <Dialog
                text={planet.name}
                position={[starMapX * 300 - 200, 0, z]}
                size={[1800, 600]}
                canvasSize={[60, 20]}
                color={
                  stage.background.dark ? [255, 255, 255, 255] : [0, 0, 0, 255]
                }
              />
            )}
          </Fragment>
        );
      })}
      <effectComposer ref={composer} args={[gl]}>
        <renderPass attachArray="passes" args={[scene, camera]} />
        {
          // @ts-ignore
          <outlinePass
            attachArray="passes"
            args={[aspect, scene, camera]}
            selectedObjects={repeat(
              Math.floor(targetLevel / NUM_LEVELS_PER_STAGE) <
                Math.floor(maxLevel / NUM_LEVELS_PER_STAGE)
                ? NUM_LEVELS_PER_STAGE
                : (maxLevel % NUM_LEVELS_PER_STAGE) + 1,
              i => planetsRef.current[i],
            )}
            visibleEdgeColor="red"
            edgeStrength={50}
            edgeThickness={0.5}
          />
        }
        {
          // @ts-ignore
          <outlinePass
            attachArray="passes"
            args={[aspect, scene, camera]}
            selectedObjects={[
              planetsRef.current[targetLevel % NUM_LEVELS_PER_STAGE],
            ]}
            visibleEdgeColor="white"
            edgeStrength={50}
            edgeThickness={0.5}
          />
        }
        {/* <shaderPass
          attachArray="passes"
          args={[]}
        /> */}
      </effectComposer>
    </>
  );
};
